import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import './searchandreplace.css';

import ReplaceForm from './replace/replace.js';

import ChevronRight from './assets/chevronright.png';
import ChevronDown from './assets/chevrondown.png';
import SearchDown from './assets/searchdown.png';
import SearchUp from './assets/searchup.png';
import Font from './assets/font.png';
import TextBox from './assets/text-box.png';

import Button from '../../../../../../components/bases/button/button.js';
import { GlobalContext } from '../../../../../../utils/globalprovider.js';

import {
  getNewWordForSearch,
  isNativeInputKey,
  trapFocusElements,
  filterTextMultipleForSearch,
} from '../../../subtitles/common/functions.js';

import { flatMapDeep, isEqual, flattenDeep, join } from 'lodash';

const SearchAndReplace = forwardRef((props, ref) => {
  const {
    st_list_ref,
    st_edit_ref,
    showSearch,
    showReplace,
    subtitles,
  } = props;

  const { language } = useContext(GlobalContext);

  const inputRef = useRef(null);

  const [text, setText] = useState('');
  const [wholeWord, setWholeWord] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [showReplaceState, setShowReplaceState] = useState(false);

  const [wordsToSearch, setWordsToSearch] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [totalWordsFounded, setTotalWordsFounded] = useState(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useImperativeHandle(ref, () => ({
    updateSearch() {
      onSearchChange(text, caseSensitive, wholeWord);
    },
  }));

  const setPosition = useCallback(
    (wordsToSearch, currentPosition, move = true) => {
      
      if (st_list_ref && st_list_ref.current) {
        st_list_ref.current.setPositionSearch(wordsToSearch, currentPosition, move);
      }

      if (st_edit_ref) {
        st_edit_ref.setPositionSearch(wordsToSearch, currentPosition, move);
      }
    },
    [st_list_ref, st_edit_ref]
  );

  useEffect(() => {
    if (showSearch) {
      onShowSearch();
    }
  }, [showSearch]);

  useEffect(() => {
    if (showReplace) {
      onShowReplace(true);
    }
  }, [showReplace]);

  useEffect(() => {
    // Initialisation du composant

    return () => {
      // Nettoyage lors du démontage
      if (st_list_ref?.current) {
        st_list_ref.current.onCloseSearch();
      }
    };
  }, [st_list_ref]);

  useEffect(() => {
    // Initialisation du composant

    return () => {
      // Nettoyage lors du démontage
      if (st_edit_ref) {
        st_edit_ref.setPositionSearch([], null, false);
      }
    };
  }, [st_edit_ref]);

  useEffect(() => {
    onSearchChange(text, caseSensitive, wholeWord, false);
  }, [subtitles]);

  const onShowSearch = useCallback(() => {
    inputRef.current?.focus();
  }, [st_list_ref, setPosition]);

  const onShowReplace = useCallback(
    (value) => {
      setShowReplaceState(value);
    },
    [st_list_ref]
  );

  const onNextPosition = useCallback(() => {
    if (!wordsToSearch.length) {
      return;
    }
    
    let currentPositionToUpdate = currentPosition;

    if (currentPosition === null || currentPosition === totalWordsFounded - 1) {
      currentPositionToUpdate = 0;
    } else {
      currentPositionToUpdate++;
    }

    setCurrentPosition(currentPositionToUpdate);
    setPosition(wordsToSearch, currentPositionToUpdate);
  }, [setPosition, wordsToSearch, currentPosition]);

  const onPrevPosition = useCallback(() => {
    if (!wordsToSearch.length) {
      return;
    }

    let currentPositionToUpdate = currentPosition;

    if (currentPosition === null || currentPosition === 0) {
      currentPositionToUpdate = totalWordsFounded - 1;
    } else {
      currentPositionToUpdate--;
    }

    setCurrentPosition(currentPositionToUpdate);
    setPosition(wordsToSearch, currentPositionToUpdate);
  }, [setPosition, wordsToSearch, currentPosition]);

  const onReplaceOne = useCallback(
    (replaceText) => {
      
      const word = wordsToSearch[currentPosition];

      if (st_list_ref && st_list_ref.current && st_list_ref.current.active) {
        st_list_ref.current.onReplaceOne(word, text, replaceText);
      } else {
        if (st_edit_ref && st_edit_ref.active) {
          st_edit_ref.onReplaceOne(word, text, replaceText);       
        }
      }

      setPosition(wordsToSearch, currentPosition, false);
    },
    [st_list_ref, setPosition, wordsToSearch, currentPosition, text, caseSensitive, wholeWord]
  );

  const onReplaceAll = useCallback(
    (replaceText) => {
      
      if (st_list_ref && st_list_ref.current && st_list_ref.current.active) {
        st_list_ref.current.onReplaceAll(text, replaceText, caseSensitive, wholeWord);
        onSearchChange(text, caseSensitive, wholeWord);
      }
    },
    [st_list_ref, setPosition, wordsToSearch, currentPosition, text, caseSensitive, wholeWord]
  );

  const onSearchChange = useCallback(
    (newText, newCaseSensitive, newWholeWord, move = true) => {

    const filterText = (callback) => {
      let wordsToSearch = [];

      subtitles.forEach((subtitle) => {
        const words = flatMapDeep(subtitle.lines.map((x) => x.words));

        const wordsFounded = words.filter(callback);

        if (wordsFounded.length) {
          wordsFounded.forEach((word) => {
            const index = words.indexOf(word);

            const newWord = getNewWordForSearch(
              newCaseSensitive,
              newWholeWord,
              index,
              word,
              newText
            );

            wordsToSearch.push({
              id: subtitle.id,
              in: subtitle.in,
              words: [newWord],
            });
          });
        }
      });

      return wordsToSearch;
    };

  const filterTextMultiple = (searchText) => {
    let wordsToSearch = [];

    subtitles.forEach((subtitle) => {
      const words = flattenDeep(subtitle.lines.map((x) => x.words));
      const sentence = join(
        words.map((x) => x.text),
        ' '
      );
      if (sentence.toLowerCase().includes(text.toLowerCase())) {
        const wordsToAdd = filterTextMultipleForSearch(
          searchText,
          subtitle,
          words,
          newCaseSensitive
        );

        if (wordsToAdd.length) {
          wordsToSearch.push(...wordsToAdd);
        }
      }
    });

    return wordsToSearch;
  };

      const wordsToSearch = [];

      if (newText.length > 0) {
        const searchWords = newText.trim().split(' ');

        if (searchWords.length > 1 && !newWholeWord) {
          wordsToSearch.push(...filterTextMultiple(newText));
        } else {
          const wordsFiltered = [];
          // Recherche d'un seul mot
          if (newCaseSensitive) {
            if (newWholeWord) {
              wordsFiltered.push(...filterText((x) => x.text === newText));
            } else {
              wordsFiltered.push(
                ...filterText((x) => x.text.includes(newText))
              );
            }
          } else if (newWholeWord) {
            wordsFiltered.push(
              ...filterText(
                (x) => x.text.toLowerCase() === newText.toLowerCase()
              )
            );
          } else {
            wordsFiltered.push(
              ...filterText((x) =>
                x.text.toLowerCase().includes(newText.toLowerCase())
              )
            );
          }

          if (wordsFiltered.length) {
            // on évite les duplications
            wordsFiltered.forEach((word) => {
              if (
                !wordsToSearch.some(
                  (x) =>
                    x.id === word.id &&
                    x.in === word.in &&
                    isEqual(x.words, word.words)
                )
              ) {
                wordsToSearch.push(word);
              }
            });
          }
        }
      }

      let newCurrentPosition =
        newText === ''
          ? null
          : text !== newText ||
              caseSensitive !== newCaseSensitive ||
              wholeWord !== newWholeWord
            ? 0
            : currentPosition;

      setWordsToSearch(wordsToSearch);
      setTotalWordsFounded(wordsToSearch.length);
      setCurrentPosition(newCurrentPosition);

      setPosition(wordsToSearch, newCurrentPosition, move);
    },
    [subtitles, setPosition]
  );

  const updateText = useCallback(
    (value) => {
      setText(value);

      if (value === '') {
        setCaseSensitive(false);
        setWholeWord(false);
      }

      onSearchChange(value, caseSensitive, wholeWord);
    },
    [caseSensitive, wholeWord, onSearchChange]
  );

  const onChange = useCallback(
    (event) => {
      updateText(event.target.value);
    },
    [updateText]
  );

  const onCaseSensitiveChange = useCallback(() => {
    const value = !caseSensitive;

    setCaseSensitive(value);

    onSearchChange(text, value, wholeWord);
  }, [caseSensitive, text, wholeWord, onSearchChange]);

  const onWholeWordChange = useCallback(() => {
    const value = !wholeWord;

    setWholeWord(value);

    onSearchChange(text, caseSensitive, value);
  }, [wholeWord, text, caseSensitive, onSearchChange]);

  const onKeyDown = useCallback(
    (event) => {
      const { key, ctrlKey, metaKey } = event;

      if (isNativeInputKey(event)) {
        event.stopPropagation();
        return;
      }

      // Entrée : résultat suivant
      if (key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        onNextPosition();
        return;
      }

      // F3 : résultat suivant
      if (key === 'F3') {
        event.preventDefault();
        event.stopPropagation();
        onNextPosition();
        return;
      }

      // F4 : résultat précédent
      if (key === 'F4') {
        event.preventDefault();
        event.stopPropagation();
        onPrevPosition();
        return;
      }

      // Ctrl+H ou Cmd+H : afficher le remplacement
      if ((ctrlKey || metaKey) && key.toLowerCase() === 'h') {
        event.preventDefault();
        event.stopPropagation();
        onShowReplace(true);
        return;
      }
    },
    [onNextPosition, onPrevPosition, onShowReplace, isNativeInputKey]
  );

  const onDialogKeyDown = useCallback((event) => {
    if (event.key !== 'Tab') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const parentElement = event.currentTarget;
    const currentElement = document.activeElement;

    trapFocusElements(
      parentElement,
      currentElement,
      'button, input, [role="button"]',
      event.shiftKey
    );
  }, []);


  return (
    <div
      role="dialog"
      className="listst-search"
      id="idSearch"
      aria-label="Rechercher ou remplacer"
      onKeyDown={onDialogKeyDown}
    >
      <div
        className={`search ${showReplaceState ? 'replace-active' : ''}`}
        role="search"
      >
        <div className="search-btn">
          <ButtonActivateReplace
            showReplace={showReplaceState}
            onShowReplace={onShowReplace}
          />
        </div>

        <div className="search-div">
          <div className="search-form">
            <label htmlFor="txtSearch" className="sr-only">
              {language === 'fr' ? 'Rechercher' : 'Search'}
            </label>

            <input
              id="txtSearch"
              type="text"
              ref={inputRef}
              placeholder={language === 'fr' ? 'Rechercher' : 'Search'}
              value={text}
              onKeyDown={onKeyDown}
              onChange={onChange}
            />

            <Button
              id="btnCaseSensitive"
              tooltip={{
                fr: 'Respecter la casse',
                en: 'Case sensitive',
              }}
              image={Font}
              width={16}
              height={16}
              toggle={true}
              toggled={caseSensitive}
              onClick={onCaseSensitiveChange}
            />

            <Button
              id="btnMotEntier"
              tooltip={{
                fr: 'Mot entier',
                en: 'Whole word',
              }}
              image={TextBox}
              width={16}
              height={16}
              toggle={true}
              toggled={wholeWord}
              onClick={onWholeWordChange}
            />

            <Button
              id="btnPrevious"
              tooltip={{
                fr: 'Correspondance précédente',
                en: 'Previous match',
              }}
              image={SearchUp}
              width={16}
              height={16}
              onClick={onPrevPosition}
            />

            <Button
              id="btnNext"
              tooltip={{
                fr: 'Correspondance suivante',
                en: 'Next match',
              }}
              image={SearchDown}
              width={16}
              height={16}
              onClick={onNextPosition}
            />
          </div>

          <ReplaceForm
            lang={language}
            showReplace={showReplaceState}
            onReplaceOne={onReplaceOne}
            onReplaceAll={onReplaceAll}
          />
        </div>
      </div>

      <div className="search-result">
        <ResultText
          lang={language}
          currentPosition={currentPosition}
          totalWordsFounded={totalWordsFounded}
          text={text}
        />
      </div>
    </div>
  );
});

function ButtonActivateReplace({ showReplace, onShowReplace }) {
  const img = showReplace ? ChevronDown : ChevronRight;

  return (
    <Button
      id="btnActivateReplace"
      tooltip={{
        fr: 'Activer/désactiver le remplacement',
        en: 'Search/Replace',
      }}
      className={showReplace ? 'replace-active' : ''}
      image={img}
      width={16}
      height={16}
      onClick={() => onShowReplace(!showReplace)}
    />
  );
}

function ResultText({ lang, currentPosition, totalWordsFounded, text }) {
  if (totalWordsFounded === undefined) {
    return <></>;
  }

  if (totalWordsFounded === 0) {
    return (
      <p className={text !== '' ? 'search-no-result' : ''}>
        {lang === 'fr' ? 'Aucun résultat' : 'No result'}
      </p>
    );
  }

  return (
    <p>
      {currentPosition === null ? 0 : currentPosition + 1}
      {lang === 'fr' ? ' sur ' : ' of '}
      {totalWordsFounded} {lang === 'fr' ? 'éléments' : 'items'}
    </p>
  );
}

export default SearchAndReplace;
